import { TCG_IN_MMM, createRowsFromConfig, generateCoordFunctions, } from "@tedivo/tedivo-bay-grid-pure";
import { BayLevelEnum, } from "open-vessel-definition";
import { assertNonNullish, feetToMillimeters, roundDec, sortNumericAsc, } from "@tedivo/tedivo-pure-helpers";
import { createSvgText } from "../helpers/createSvgText";
import { drawDeckBridge } from "./drawParts/drawDeckBridge";
import { drawDeckSmokeStack } from "./drawParts/drawDeckSmokeStack";
import { generateTopContainerSymbols } from "./generateTopContainerSymbols";
import { lidDataForBaysValidatorFn } from "@tedivo/tedivo-ovd-validators";
import { makeHull } from "../helpers/convexHull";
import { setTopBayNumbers } from "../helpers/setTopBayNumbers";
export function createSimpleDeckView({ sizeSummary, baysData, masterCGs, lidData, bayLcvVcgTcgAndPairings, symbolsOptions, vesselPartsData, containersWithPositions, doDrawVesselParts = true, selectContainerCriteria = "highestBelowOrLowestAbove", bayNumbersDoubleClickAction, svgGroupId = "createSimpleDeckView-g", }) {
    const { bayLevelPositionsAbove, bayLevelPositionsBelow, missingImportantVcgs, missingImportantLcgs, missingImportantTcgs, minTierAbove, lcgsBy20Bay, cgStats: { totalSlotsCount }, } = bayLcvVcgTcgAndPairings;
    const missingImportantXcgs = missingImportantTcgs || missingImportantLcgs || missingImportantVcgs;
    // 1. Add missing LCGs and VCGs if needed, return x,y functions
    const coordFns = generateCoordFunctions({
        sizeSummary,
        baysData,
        vesselPartsData,
        bayLcvVcgTcgAndPairings,
        maxHeightAbsolute: containersWithPositions.maxHeightAbsolute,
    });
    assertNonNullish(coordFns, "Could not generate coord functions");
    const { xRange, zRange, xPos, zPos, scaled, addX, addZ, svgSizeRatio, allPartsLengthInMeters, sternLength, vOneBaySlots, } = coordFns;
    // 2. SVG Node
    const svgNode = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    if (xRange)
        svgNode.setAttribute("width", String(xRange));
    if (zRange)
        svgNode.setAttribute("height", String(zRange));
    if (xRange && zRange)
        svgNode.setAttribute("viewBox", `0 0 ${xRange} ${zRange}`);
    const svgGroup = document.createElementNS("http://www.w3.org/2000/svg", "g");
    svgGroup.setAttribute("id", svgGroupId);
    // 3. Add symbols
    const symbols = generateTopContainerSymbols(symbolsOptions || {}, svgSizeRatio);
    Object.keys(symbols).forEach((len) => {
        svgGroup.appendChild(symbols[len]);
    });
    // 4. Draw Vessel Parts
    drawVesselParts(doDrawVesselParts);
    // 5. Top bay numbers
    const doNotRepeatBays = new Set();
    const drawTopBayNumbers = (blps) => setTopBayNumbers({
        blps,
        lcgsBy20Bay,
        svgNode: svgGroup,
        xPos,
        fontColor: symbolsOptions && symbolsOptions.fontColor,
        onDoubleClick: bayNumbersDoubleClickAction,
        doNotRepeatBays,
    });
    if (bayLevelPositionsAbove.length)
        drawTopBayNumbers(bayLevelPositionsAbove);
    else
        drawTopBayNumbers(bayLevelPositionsBelow);
    if (bayLevelPositionsAbove.length)
        drawTopBayNumbers(bayLevelPositionsAbove);
    else
        drawTopBayNumbers(bayLevelPositionsBelow);
    let validStats = {
        ok: 0,
        warning: 0,
        danger: 0,
    };
    if (totalSlotsCount > 0) {
        // 6. Ship Contour
        generateShipContour(containersWithPositions);
        // 7. Draw containers
        drawDeckContainers(minTierAbove, containersWithPositions, selectContainerCriteria);
        //blPosBelowOrAboveIfMissing.forEach(generateContainers);
        // 8. Draw Lids
        const validStatsRet = drawLids(lcgsBy20Bay);
        if (validStatsRet)
            validStats = validStatsRet;
    }
    svgNode.appendChild(svgGroup);
    return {
        deckViewSvg: svgNode,
        missingImportantXcgs,
        xRange,
        zRange,
        xPos,
        zPos,
        lcgsBy20Bay,
        addX,
        validStats,
    };
    function drawDeckContainers(minTierAbove, containersWithPositions, selectContainerCriteria) {
        const { containerCgsByBayAndLevelAndRow } = containersWithPositions;
        const subsetOfContainersToDraw = selectContainersToDraw(selectContainerCriteria);
        subsetOfContainersToDraw
            .sort((a, b) => (Number(b.bottomTier) || 0) - (Number(a.bottomTier) || 0))
            .forEach((c) => {
            if (c.lcg === undefined ||
                c.tcg === undefined ||
                c.meta === undefined ||
                c.bottomTier === undefined)
                return;
            const x = xPos(c.lcg);
            const y = zPos(c.tcg);
            const isAbove = Number(c.meta.tier) >= minTierAbove;
            const contSvg = document.createElementNS("http://www.w3.org/2000/svg", "use");
            contSvg.setAttribute("href", `#dmbCn${c.size}`);
            contSvg.setAttribute("x", `${x}`);
            contSvg.setAttribute("y", `${y}`);
            if (isAbove && (symbolsOptions === null || symbolsOptions === void 0 ? void 0 : symbolsOptions.dimmedStrokeColor)) {
                contSvg.setAttribute("stroke", symbolsOptions.dimmedStrokeColor);
                contSvg.setAttribute("stroke-opacity", "0.33");
            }
            contSvg.setAttribute("data-p", `${c.isoOddBay}-${c.meta.row}`);
            contSvg.setAttribute("data-l", `${c.meta.level}`);
            svgGroup.appendChild(contSvg);
            if (symbolsOptions === null || symbolsOptions === void 0 ? void 0 : symbolsOptions.addRowLines) {
                const textColor = (c.meta.row === "00"
                    ? symbolsOptions === null || symbolsOptions === void 0 ? void 0 : symbolsOptions.fontColor
                    : Number(c.meta.row) % 2 === 0
                        ? symbolsOptions === null || symbolsOptions === void 0 ? void 0 : symbolsOptions.portColor
                        : symbolsOptions === null || symbolsOptions === void 0 ? void 0 : symbolsOptions.stbdColor) || (symbolsOptions === null || symbolsOptions === void 0 ? void 0 : symbolsOptions.fontColor);
                const stckNumber = createSvgText({
                    text: c.meta.row,
                    x: 0,
                    y: 0,
                    fontSize: 7,
                    textColor: !isAbove
                        ? textColor
                        : (symbolsOptions === null || symbolsOptions === void 0 ? void 0 : symbolsOptions.dimmedStrokeColor) || "#ddd",
                });
                stckNumber.setAttribute("transform", `rotate(90, 0,0) translate(${y}, ${-x} )`);
                svgGroup.appendChild(stckNumber);
            }
        });
        function selectContainersToDraw(selectContainerCriteria) {
            const byBayAndLevelAndRowKeys = Object.keys(containerCgsByBayAndLevelAndRow);
            const isoBaysAndRows = byBayAndLevelAndRowKeys
                .map((key) => {
                const [bay, , row] = key.split("-");
                return `${bay}-${row}`;
            })
                .filter((v, i, a) => a.indexOf(v) === i)
                .sort()
                .map((s) => s.split("-"));
            const subsetOfContainersToDraw = [];
            switch (selectContainerCriteria) {
                case "highestBelowOrLowestAbove":
                    {
                        // Get the highest container below
                        isoBaysAndRows.forEach(([bay, row]) => {
                            const contsBelow = containerCgsByBayAndLevelAndRow[`${bay}-${BayLevelEnum.BELOW}-${row}`];
                            if (contsBelow && contsBelow.length) {
                                subsetOfContainersToDraw.push(contsBelow[0]);
                                return;
                            }
                            // If there are no containers below, get the lowest above
                            const contsAbove = containerCgsByBayAndLevelAndRow[`${bay}-${BayLevelEnum.ABOVE}-${row}`];
                            if (contsAbove && contsAbove.length) {
                                subsetOfContainersToDraw.push(contsAbove[contsAbove.length - 1]);
                            }
                        });
                    }
                    break;
                case "highestAll":
                    {
                        isoBaysAndRows.forEach(([bay, row]) => {
                            // Get the highest container on every bay. First above
                            const contsAbove = containerCgsByBayAndLevelAndRow[`${bay}-${BayLevelEnum.ABOVE}-${row}`];
                            if (contsAbove && contsAbove.length) {
                                subsetOfContainersToDraw.push(contsAbove[contsAbove.length - 1]);
                                return;
                            }
                            // If there are no containers above, get the highest below
                            const contsBelow = containerCgsByBayAndLevelAndRow[`${bay}-${BayLevelEnum.BELOW}-${row}`];
                            if (contsBelow && contsBelow.length) {
                                subsetOfContainersToDraw.push(contsBelow[contsBelow.length - 1]);
                                return;
                            }
                        });
                    }
                    break;
                case "highestBelow":
                    {
                        // Get the highest container below
                        isoBaysAndRows.forEach(([bay, row]) => {
                            const contsBelow = containerCgsByBayAndLevelAndRow[`${bay}-${BayLevelEnum.BELOW}-${row}`];
                            if (contsBelow && contsBelow.length) {
                                subsetOfContainersToDraw.push(contsBelow[0]);
                            }
                        });
                    }
                    break;
                default: {
                    const tierSelected = selectContainerCriteria;
                    const tierIsAbove = Number(tierSelected) >= minTierAbove;
                    isoBaysAndRows.forEach(([bay, row]) => {
                        const conts = containerCgsByBayAndLevelAndRow[`${bay}-${tierIsAbove ? BayLevelEnum.ABOVE : BayLevelEnum.BELOW}-${row}`];
                        if (conts) {
                            const c = conts.find((c) => { var _a; return ((_a = c.meta) === null || _a === void 0 ? void 0 : _a.tier) === tierSelected; });
                            if (c)
                                subsetOfContainersToDraw.push(c);
                        }
                    });
                }
            }
            return subsetOfContainersToDraw;
        }
    }
    function generateShipContour(containersWithPositions) {
        const { bayLevelCgsInfo } = containersWithPositions;
        const depthInMilimeters = roundDec(TCG_IN_MMM * svgSizeRatio, 1);
        const segmentsPort = [];
        const segmentsStbd = [];
        let prevPortTcg = 0;
        let prevStarboardTcg = 0;
        const bays = Object.keys(bayLevelCgsInfo).sort(sortNumericAsc);
        bays.forEach((isoBay) => {
            var _a, _b, _c, _d, _e, _f, _g, _h;
            const portBorderFromBL = Math.min(((_a = bayLevelCgsInfo[isoBay][BayLevelEnum.BELOW]) === null || _a === void 0 ? void 0 : _a.portTcg) || 0, ((_b = bayLevelCgsInfo[isoBay][BayLevelEnum.ABOVE]) === null || _b === void 0 ? void 0 : _b.portTcg) || 0);
            const stbdBorderFromBL = Math.max(((_c = bayLevelCgsInfo[isoBay][BayLevelEnum.BELOW]) === null || _c === void 0 ? void 0 : _c.starboardTcg) || 0, ((_d = bayLevelCgsInfo[isoBay][BayLevelEnum.ABOVE]) === null || _d === void 0 ? void 0 : _d.starboardTcg) || 0);
            const portBorder = roundDec(portBorderFromBL - depthInMilimeters * 0.5, 2) - 2000;
            const stbdBorder = roundDec(stbdBorderFromBL + depthInMilimeters * 0.5, 2) + 2000;
            // Add parts of line
            const fLcg = Math.max(((_e = bayLevelCgsInfo[isoBay][BayLevelEnum.ABOVE]) === null || _e === void 0 ? void 0 : _e.maxForeLcg) || 0, ((_f = bayLevelCgsInfo[isoBay][BayLevelEnum.BELOW]) === null || _f === void 0 ? void 0 : _f.maxForeLcg) || 0);
            const aLcg = Math.min(((_g = bayLevelCgsInfo[isoBay][BayLevelEnum.ABOVE]) === null || _g === void 0 ? void 0 : _g.minAftLcg) || 0, ((_h = bayLevelCgsInfo[isoBay][BayLevelEnum.BELOW]) === null || _h === void 0 ? void 0 : _h.minAftLcg) || 0);
            // Port side
            segmentsPort.push([xPos(fLcg, 1), zPos(portBorder, 1)]);
            segmentsPort.push([xPos(aLcg, 1), zPos(portBorder, 1)]);
            // Stbd side
            segmentsStbd.push([xPos(fLcg, 1), zPos(stbdBorder, 1)]);
            segmentsStbd.push([xPos(aLcg, 1), zPos(stbdBorder, 1)]);
            if (prevPortTcg > portBorder)
                prevPortTcg = portBorder;
            if (prevStarboardTcg < stbdBorder)
                prevStarboardTcg = stbdBorder;
        });
        // Finally, add stern
        segmentsPort.push([roundDec(addX * 0.25), zPos(prevPortTcg, 1)]);
        segmentsStbd.push([roundDec(addX * 0.25), zPos(prevStarboardTcg, 1)]);
        const bow = [
            [roundDec(segmentsPort[0][0]), roundDec(segmentsPort[0][1])],
            [xRange, roundDec(zRange * 0.5)],
            [roundDec(segmentsStbd[0][0]), roundDec(segmentsStbd[0][1])],
        ];
        const allSegments = [
            ...segmentsPort.reverse(),
            ...bow,
            ...segmentsStbd,
        ].map(([x, y]) => ({ x, y }));
        const uniqueSegments = makeHull(allSegments).map((point) => [
            point.x,
            point.y,
        ]);
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const initialPoint = uniqueSegments.shift();
        const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
        path.setAttribute("stroke", (symbolsOptions === null || symbolsOptions === void 0 ? void 0 : symbolsOptions.shipStrokeColor) || "red");
        path.setAttribute("stroke-width", String((symbolsOptions === null || symbolsOptions === void 0 ? void 0 : symbolsOptions.strokeWidth) || 0.1));
        path.setAttribute("fill-opacity", "0");
        path.setAttribute("d", `M${initialPoint[0]},${initialPoint[1]} ${uniqueSegments
            .map((seg) => `L${seg[0]},${seg[1]}`)
            .join(" ")} z`);
        svgGroup.appendChild(path);
    }
    function drawLids(lcgsBy20Bay) {
        const halfDepthInMilimeters = roundDec(TCG_IN_MMM * 0.5, 1); // Doesn't use svgSizeRatio because xPos is applied later
        if (sizeSummary.maxRow === undefined)
            return;
        const { bayLevelPositionsBelow, bayLevelPositionsAbove } = bayLcvVcgTcgAndPairings;
        const validStats = {
            ok: 0,
            warning: 0,
            danger: 0,
        };
        // Validate lids
        const { result: { rowTcgsByBay, allLids: lidsDataWithValidation }, } = lidDataForBaysValidatorFn({
            data: {
                json: { sizeSummary, lidData },
                lcgVcgTcgAndPairings: bayLcvVcgTcgAndPairings,
                lcgsBy20Bay,
            },
        });
        /** Make a calculation of all the TCGs in the ship to use if missing (lids over non-existing rows in bay) */
        const tempMasterAllTcgs = {};
        if (!missingImportantTcgs) {
            [...bayLevelPositionsBelow, ...bayLevelPositionsAbove].forEach((blp) => {
                const rows = Object.keys(blp.perRowAverageBayTcg).sort();
                rows.forEach((row) => {
                    const maxTcg = Math.abs(tempMasterAllTcgs[row] || 0);
                    const tcg = blp.perRowAverageBayTcg.get(row);
                    if (maxTcg < Math.abs(tcg || 0))
                        tempMasterAllTcgs[row] = tcg || 0;
                });
            });
        }
        else {
            const rows = createRowsFromConfig(!!sizeSummary.centerLineRow, sizeSummary.maxRow);
            rows.forEach((row) => {
                const maxTcg = Math.abs(tempMasterAllTcgs[row] || 0);
                const tcg = bayLcvVcgTcgAndPairings.getMissingTcg(row);
                if (maxTcg < Math.abs(tcg || 0))
                    tempMasterAllTcgs[row] = tcg || 0;
            });
        }
        // Draw lids
        lidsDataWithValidation
            .filter((lid) => lcgsBy20Bay[lid.startIsoBay] !== undefined &&
            lcgsBy20Bay[lid.endIsoBay] !== undefined)
            .forEach((lid, idx) => {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p;
            const lcgs20StartIsoBay = (_b = (_a = lcgsBy20Bay[lid.startIsoBay].lcgs) === null || _a === void 0 ? void 0 : _a.get) === null || _b === void 0 ? void 0 : _b.call(_a, 20);
            const lcgs20EndIsoBay = (_d = (_c = lcgsBy20Bay[lid.endIsoBay].lcgs) === null || _c === void 0 ? void 0 : _c.get) === null || _d === void 0 ? void 0 : _d.call(_c, 20);
            if (lcgs20StartIsoBay === undefined || lcgs20EndIsoBay === undefined)
                return;
            const foreLcg = lcgs20StartIsoBay.foreLcg;
            const aftLcg = lcgs20EndIsoBay.aftLcg;
            let portTcg = (_h = (_f = (_e = rowTcgsByBay[lid.startIsoBay]) === null || _e === void 0 ? void 0 : _e[lid.portIsoRow]) !== null && _f !== void 0 ? _f : (_g = rowTcgsByBay[lid.endIsoBay]) === null || _g === void 0 ? void 0 : _g[lid.portIsoRow]) !== null && _h !== void 0 ? _h : tempMasterAllTcgs[lid.portIsoRow];
            let stbdTcg = (_m = (_k = (_j = rowTcgsByBay[lid.startIsoBay]) === null || _j === void 0 ? void 0 : _j[lid.starboardIsoRow]) !== null && _k !== void 0 ? _k : (_l = rowTcgsByBay[lid.endIsoBay]) === null || _l === void 0 ? void 0 : _l[lid.starboardIsoRow]) !== null && _m !== void 0 ? _m : tempMasterAllTcgs[lid.starboardIsoRow];
            if (portTcg === undefined) {
                portTcg =
                    (_o = masterCGs.belowTcgs[lid.portIsoRow]) !== null && _o !== void 0 ? _o : masterCGs.aboveTcgs[lid.portIsoRow];
            }
            if (stbdTcg === undefined) {
                stbdTcg =
                    (_p = masterCGs.belowTcgs[lid.starboardIsoRow]) !== null && _p !== void 0 ? _p : masterCGs.aboveTcgs[lid.starboardIsoRow];
            }
            if (foreLcg !== undefined &&
                aftLcg !== undefined &&
                portTcg !== undefined &&
                stbdTcg !== undefined) {
                portTcg -= halfDepthInMilimeters;
                stbdTcg += halfDepthInMilimeters;
                const lidColor = lid.notValid === "danger"
                    ? (symbolsOptions === null || symbolsOptions === void 0 ? void 0 : symbolsOptions.lidDangerFillColor) || "red"
                    : lid.notValid === "warning"
                        ? (symbolsOptions === null || symbolsOptions === void 0 ? void 0 : symbolsOptions.lidWarningFillColor) || "yellow"
                        : (symbolsOptions === null || symbolsOptions === void 0 ? void 0 : symbolsOptions.lidFillColor) || "black";
                if (lid.notValid !== undefined)
                    validStats[lid.notValid]++;
                else
                    validStats.ok++;
                const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
                path.setAttribute("stroke", (symbolsOptions === null || symbolsOptions === void 0 ? void 0 : symbolsOptions.shipStrokeColor) || "red");
                path.setAttribute("stroke-width", String((symbolsOptions === null || symbolsOptions === void 0 ? void 0 : symbolsOptions.strokeWidth) || 0.1));
                path.setAttribute("fill", lidColor);
                path.setAttribute("fill-opacity", "0.4");
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                path.setAttribute("data-idx", lid.pk || String(idx));
                path.setAttribute("data-z", String(Math.round((xPos(aftLcg) + xPos(foreLcg)) * 0.5)));
                path.setAttribute("data-x", String(Math.round((zPos(portTcg) + zPos(stbdTcg)) * 0.5)));
                path.setAttribute("d", `M${xPos(aftLcg, 2)},${zPos(portTcg)}
              L${xPos(foreLcg, 2)},${zPos(portTcg)}
              L${xPos(foreLcg, 2)},${zPos(stbdTcg)}
              L${xPos(aftLcg, 2)},${zPos(stbdTcg)}
              z`);
                const xText = xPos((aftLcg + foreLcg) * 0.5, 2), yText = zPos((portTcg + stbdTcg) * 0.5, 2);
                const title = createSvgText({
                    text: lid.label,
                    x: 0,
                    y: (symbolsOptions === null || symbolsOptions === void 0 ? void 0 : symbolsOptions.addRowLines) ? 4 : 0,
                    textColor: (symbolsOptions === null || symbolsOptions === void 0 ? void 0 : symbolsOptions.lidTextColor) || "red",
                    fontSize: 8,
                });
                title.setAttribute("transform", `rotate(90, 0,0) translate(${yText}, ${-xText} )`);
                if (lid.overlapPort) {
                    const overlapPort = document.createElementNS("http://www.w3.org/2000/svg", "line");
                    overlapPort.setAttribute("stroke", (symbolsOptions === null || symbolsOptions === void 0 ? void 0 : symbolsOptions.shipStrokeColor) || "red");
                    overlapPort.setAttribute("stroke-width", String(((symbolsOptions === null || symbolsOptions === void 0 ? void 0 : symbolsOptions.strokeWidth) || 0.1) * 4));
                    overlapPort.setAttribute("x1", String(xPos(aftLcg, 2)));
                    overlapPort.setAttribute("y1", String(zPos(portTcg, 2)));
                    overlapPort.setAttribute("x2", String(xPos(foreLcg, 2)));
                    overlapPort.setAttribute("y2", String(zPos(portTcg, 2)));
                    overlapPort.setAttribute("stroke-linejoin", "round");
                    svgGroup.appendChild(overlapPort);
                }
                if (lid.overlapStarboard) {
                    const overlapStbd = document.createElementNS("http://www.w3.org/2000/svg", "line");
                    overlapStbd.setAttribute("stroke", (symbolsOptions === null || symbolsOptions === void 0 ? void 0 : symbolsOptions.shipStrokeColor) || "red");
                    overlapStbd.setAttribute("stroke-width", String(((symbolsOptions === null || symbolsOptions === void 0 ? void 0 : symbolsOptions.strokeWidth) || 0.1) * 4));
                    overlapStbd.setAttribute("x1", String(xPos(aftLcg, 2)));
                    overlapStbd.setAttribute("y1", String(zPos(stbdTcg, 2)));
                    overlapStbd.setAttribute("x2", String(xPos(foreLcg, 2)));
                    overlapStbd.setAttribute("y2", String(zPos(stbdTcg, 2)));
                    svgGroup.appendChild(overlapStbd);
                }
                svgGroup.appendChild(path);
                svgGroup.appendChild(title);
            }
        });
        return validStats;
    }
    function drawVesselParts(doDrawVesselParts) {
        if (!doDrawVesselParts)
            return;
        const rightOfShip = scaled(allPartsLengthInMeters + sternLength) + addX;
        // console.log(
        //   "rightOfShip",
        //   { allPartsLengthInMeters, bowLength, sternLength },
        //   scaled(allPartsLengthInMeters + bowLength + sternLength),
        // );
        // const path2 = document.createElementNS(
        //   "http://www.w3.org/2000/svg",
        //   "path",
        // );
        // const ps: number[] = [];
        // vOneBaySlots.forEach((vOneBay, idx) => {
        //   const posX100 = vOneBay.positionX * 100;
        //   ps.push(feetToMillimeters(posX100));
        // });
        // console.log(ps);
        // path2.setAttribute("stroke", "red");
        // path2.setAttribute("stroke-width", "1");
        // path2.setAttribute(
        //   "d",
        //   ps.reduce((acc, po, idx) => {
        //     const p = rightOfShip - scaled(po);
        //     return `${acc} M${p},0 L${p},${yRange} `;
        //   }, ""),
        // );
        // svgNode.appendChild(path2);
        vOneBaySlots.forEach((vOneBay, idx) => {
            var _a, _b;
            if (vOneBay.type === "BAY")
                return;
            const posX100 = vOneBay.positionX * 100;
            const nextPosX100 = ((_a = vOneBaySlots[idx + 1]) === null || _a === void 0 ? void 0 : _a.positionX) * 100;
            let xtra = 0;
            const nextLen = (_b = vOneBaySlots[idx + 1]) === null || _b === void 0 ? void 0 : _b.len;
            if (nextLen !== undefined && nextLen > 40) {
                xtra = scaled(feetToMillimeters(nextLen - 40) * 0.5);
            }
            const cPosX = feetToMillimeters(posX100);
            const nPosX = feetToMillimeters(nextPosX100 !== undefined ? nextPosX100 - xtra : posX100 + vOneBay.len);
            const x = rightOfShip - scaled(nPosX * 0.5 + cPosX * 0.5);
            let paths = undefined;
            switch (vOneBay.type) {
                case "BRG":
                    paths = drawDeckBridge(vOneBay, zRange, addZ, svgSizeRatio);
                    break;
                case "SMK":
                    paths = drawDeckSmokeStack(vOneBay, zRange, addZ, svgSizeRatio);
                    break;
                // case "CRN":
                //   paths = drawSideCrane(vOneBay, midVcg, svgSizeRatio);
                //   break;
            }
            if (paths) {
                paths.forEach((p) => {
                    p.setAttribute("transform", `translate(${x}, ${0})`);
                    svgNode.appendChild(p);
                });
            }
        });
    }
}
